// GtCandidateRecord — the candidate record. A profile header (identity, quick
// facts, actions and a stat strip) over two columns: tabs and AI questions on
// the left, key information, enrichment, benches and files on the right.
// Admin (pool record, compliance, merge and erasure) lives under Record.
// Everything inside .gtr2 is set at 12px; see the gtr2 block in gt.css.
import GtAiCard from '../../components/GtAiCard';
import GtDossierView from '../../components/GtDossierView';
import { GtEnrichCard } from '../../components/GtEnrich';
import { downloadDossier } from '../../components/gtDossier';
import { Fragment } from 'react';
import GtNoticeModal from '../../components/GtNoticeModal';
import { gtConfirm } from '../../components/GtConfirm';
import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import GtAddToBenchModal from '../../components/GtAddToBenchModal';
import GtLoader from '../../components/GtLoader';
import API_BASE from '../../config';
import { GtSelect, GtDatePicker } from '../../components/GtControls';
import GtEditor from '../../components/GtEditor';
import GtInterviewsPanel from '../../components/GtInterviewsPanel';
import GtCandidateForms from '../../components/GtCandidateForms';
import GtCandidateLexa from '../../components/GtCandidateLexa'; // C1-LEXA5
import GtEmailModal from '../../components/GtEmailModal'; // C1-EMAIL
import GtCandidateEmails from '../../components/GtCandidateEmails';
import { TempHistory } from '../../components/gtTemp'; // C1-TEMPHIST
import GtCandidateSequences from '../../components/GtCandidateSequences'; // C1-SEQ3

function initials(name) { return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase(); }
const AVATAR_COLOURS = ['#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E', '#0F766E', '#B91C1C', '#5D4037', '#3949AB', '#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVATAR_COLOURS[h % AVATAR_COLOURS.length]; }
function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function relTime(v) { if (!v) return ''; const d = new Date(v); if (isNaN(d)) return ''; const diff = Date.now() - d.getTime(); const day = 86400000; if (diff < day) return 'Today'; if (diff < 2 * day) return 'Yesterday'; if (diff < 30 * day) return Math.floor(diff / day) + ' days ago'; return ukDate(v); }
function formatRate(gbp, unit) { if (gbp == null || gbp === '') return null; const n = Number(gbp); if (!isFinite(n)) return null; const s = unit === 'year' ? '/yr' : unit === 'day' ? '/day' : unit === 'hour' ? '/hr' : ''; return '£' + Math.round(n).toLocaleString('en-GB') + s; }

const STATUS_PILL = {
  new: 'gtt-p-slate', parsing: 'gtt-p-slate', enriched: 'gtt-p-blue', invited_to_interview: 'gtt-p-amber',
  interviewing: 'gtt-p-amber', assessed: 'gtt-p-blue', available: 'gtt-p-green', submitted: 'gtt-p-teal',
  placed: 'gtt-p-green', archived: 'gtt-p-slate',
};
const RTW_LABEL = { citizen: 'Citizen', permanent_resident: 'Permanent resident', visa_holder: 'Visa holder', needs_sponsorship: 'Needs sponsorship', unknown: 'Unknown' };
const LANE_LABEL = { self_serve: 'Self serve', bulk_import: 'Bulk import', manual: 'Manual add', bulk_cv: 'Bulk CV parse' };
const EDIT_FIELDS = [
  ['full_name', 'Full name'], ['headline', 'Headline'], ['email', 'Email'], ['phone', 'Phone'],
  ['location', 'Location'], ['linkedin_url', 'LinkedIn URL'], ['github_username', 'GitHub username'],
  ['current_company', 'Company'], ['current_title', 'Title'],
  ['right_to_work_status', 'Right to work'], ['right_to_work_country', 'RTW country (e.g. GB)'],
  ['expected_rate_gbp', 'Expected rate (GBP)'], ['expected_rate_unit', 'Rate unit'],
  ['consent_status', 'Consent'],
  ['availability_date', 'Available from'], ['notice_period', 'Notice period'],
];
const EDIT_SELECTS = {
  right_to_work_status: ['', 'citizen', 'permanent_resident', 'visa_holder', 'needs_sponsorship', 'unknown'],
  expected_rate_unit: ['', 'year', 'day', 'hour'],
  consent_status: ['none', 'explicit', 'withdrawn'],
  notice_period: ['', 'immediate', '1 week', '2 weeks', '1 month', '2 months', '3 months', '3+ months'],
};
const EDIT_DATES = { availability_date: true };
const TABS = ['Activity', 'CV', 'Notes', 'Emails', 'Applications', 'Interviews', 'Lexa', 'Forms', 'Sequences', 'Record']; // C1-EMAIL: Emails
// C1-EMAIL: note categories, and whether the note records us reaching out or them replying.
const NOTE_CATS = [['general', 'General'], ['linkedin', 'LinkedIn'], ['call', 'Call'], ['email', 'Email'], ['meeting', 'Meeting']];
const NOTE_CAT_LABEL = Object.fromEntries(NOTE_CATS);

export default function GtCandidateRecord({ candidateId, getToken, onBack, userName, userImage, backLabel }) {
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesHasMore, setNotesHasMore] = useState(false);
  const [notesCursor, setNotesCursor] = useState(null);
  const [notesLoadingMore, setNotesLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Activity');
  // Opportunity email: staff pick one open role and tell this candidate about
  // it. The result is reported verbatim, including a refusal, because a skip
  // means the candidate's own preference stopped it and that is worth seeing.
  const [oppRoles, setOppRoles] = useState([]);
  const [oppRole, setOppRole] = useState('');
  const [oppNote, setOppNote] = useState('');
  const [oppBusy, setOppBusy] = useState(false);
  const [oppResult, setOppResult] = useState(null);
  const [oppDrafting, setOppDrafting] = useState(false);
  const [oppDraftError, setOppDraftError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteCat, setNoteCat] = useState('general'); // C1-EMAIL
  const [noteDir, setNoteDir] = useState(null);
  const [noteFilter, setNoteFilter] = useState('all');
  const [emailOpen, setEmailOpen] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [toast, setToast] = useState(null);
  const [myClerkId, setMyClerkId] = useState(null);
  const [cvUrl, setCvUrl] = useState(null);
  const [cvIsPdf, setCvIsPdf] = useState(false);
  const [showRawCv, setShowRawCv] = useState(false);
  const [suppression, setSuppression] = useState(null);
  const [docs, setDocs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [apps, setApps] = useState([]);
  // Each application moves through its role's own stages, like the board.
  const [appStages, setAppStages] = useState({});
  const [appReject, setAppReject] = useState(null);
  const [appReason, setAppReason] = useState('');
  const [appNoticeIds, setAppNoticeIds] = useState(null);
  const [appBusy, setAppBusy] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewBusy, setReviewBusy] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [docKind, setDocKind] = useState('other');
  const [docBusy, setDocBusy] = useState(false);
  const [mergeId, setMergeId] = useState('');
  const [dangerBusy, setDangerBusy] = useState(false);
  const [impBusy, setImpBusy] = useState(false);
  // Which header menu is open: 'pdf', 'more' or none. Escape closes it.
  const [menu, setMenu] = useState(null);
  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);

  // Acting as a candidate is admin and above only, and there must be a portal
  // account to act as. A sourced candidate who never signed up has none.
  const { user: viewer } = useUser();
  const viewerRole = viewer && viewer.publicMetadata && viewer.publicMetadata.role;
  const canImpersonate = (viewerRole === 'super_admin' || viewerRole === 'admin') && !!(c && c.clerk_user_id);
  // C1-LEXA5: Lexa coaching is visible to admins and super admins only.
  const canSeeLexa = viewerRole === 'super_admin' || viewerRole === 'admin';

  // The grant lives in sessionStorage, not localStorage: closing the tab must
  // end the session rather than leave it lying around on a shared machine.


  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, {
      ...opts,
      headers: {
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(opts.headers || {}),
        Authorization: 'Bearer ' + token,
      },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || body.error || ('request failed: ' + res.status));
    return body;
  }, [getToken]);

  const startImpersonation = useCallback(async () => {
    setImpBusy(true);
    try {
      // authFetch returns the parsed body and THROWS on a non-ok response,
      // so every failure is handled in the catch below.
      const data = await authFetch('/api/candidates/' + candidateId + '/impersonate', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      sessionStorage.setItem('gt_impersonation', JSON.stringify({
        token: data.token,
        expires_at: data.expires_at,
        candidate: data.candidate,
      }));
      window.location.reload();
    } catch (e) {
      setToast(e.message || 'Could not open the candidate view.');
    } finally {
      setImpBusy(false);
    }
  }, [authFetch, candidateId]);

  function flash(msg) { setToast(msg); setTimeout(() => setToast(null), 2600); }

  // Benches this person is on. A bench never contacts them.
  const navigate = useNavigate();
  const [pdfBusy, setPdfBusy] = useState(null);
  const [pdfErr, setPdfErr] = useState(null);
  const [dossierOpen, setDossierOpen] = useState(false);
  async function makePdf(anonymous) {
    setPdfBusy(anonymous ? 'anon' : 'named'); setPdfErr(null);
    try {
      await downloadDossier(getToken, candidateId, { name: c && c.full_name, refLabel: c && c.ref, anonymous });
    } catch (e) { setPdfErr(e.message || 'Could not make the PDF.'); }
    setPdfBusy(null);
  }
  const [onBenches, setOnBenches] = useState(null);
  const [benchAdd, setBenchAdd] = useState(false);
  const loadBenches = useCallback(async () => {
    try { const r = await authFetch('/api/candidates/' + candidateId + '/benches'); setOnBenches(r.benches || []); }
    catch (e) { setOnBenches([]); }
  }, [authFetch, candidateId]);
  useEffect(() => { loadBenches(); }, [loadBenches]);

  const moveReview = useCallback(async (reqId, status, note) => {
    setReviewBusy(reqId);
    try {
      const r = await authFetch('/api/candidates/' + candidateId + '/human-review/' + reqId, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, resolution_note: note || null }),
      });
      setReviews(r.requests || []);
      setReviewNote('');
      flash(status === 'closed' ? 'Review closed' : 'Review picked up');
      try {
        const av = await authFetch('/api/candidates/' + candidateId + '/activity?limit=60');
        setActivity(av.events || []);
      } catch (e) { /* feed refresh is non-fatal */ }
    } catch (e) {
      flash(e.message || 'Could not update the request');
    } finally { setReviewBusy(null); }
  }, [authFetch, candidateId]);

  // Moving an application writes to the event spine, so it lands on this
  // candidate's Activity feed AND moves their progress rail in the portal.
  // Choosing a role IS the instruction to draft. Nobody wants to pick a role and
  // then press a second button to get the thing they picked it for.
  const chooseOppRole = useCallback(async (value) => {
    setOppRole(value);
    setOppResult(null);
    setOppDraftError(null);
    setOppNote('');
    if (!value) return;
    setOppDrafting(true);
    try {
      const r = await authFetch('/api/candidates/' + candidateId + '/opportunity-draft', {
        method: 'POST',
        body: JSON.stringify({ role_id: Number(value) }),
      });
      setOppNote((r && r.html) || '');
    } catch (e) {
      setOppDraftError((e && e.message) || 'Could not draft the message.');
    } finally {
      setOppDrafting(false);
    }
  }, [authFetch, candidateId]);

  const sendOpportunity = useCallback(async () => {
    if (!oppRole) return;
    setOppBusy(true);
    setOppResult(null);
    try {
      const r = await authFetch('/api/candidates/' + candidateId + '/notify-opportunity', {
        method: 'POST',
        body: JSON.stringify({ role_id: Number(oppRole), note: oppNote.trim() || undefined }),
      });
      setOppResult(r);
      if (r && r.sent) { setOppNote(''); setOppRole(''); }
    } catch (e) {
      setOppResult({ sent: false, skipped: false, reason: (e && e.message) || 'failed' });
    } finally {
      setOppBusy(false);
    }
  }, [authFetch, candidateId, oppRole, oppNote]);

  // Moves them on the role's board. What the candidate sees follows from the
  // stage, and nobody is emailed: if their portal changes, the box asks first.
  const moveApp = useCallback(async (application, stage, reason, stageList) => {
    const target = (stageList || []).find(x => x.key === stage);
    if (target && target.outcome === 'lost' && !(reason && reason.trim())) {
      setAppReject({ id: application.id, stage }); setAppReason(''); return;
    }
    setAppBusy(application.id);
    try {
      const r = await authFetch('/api/candidates/' + candidateId + '/applications/' + application.id, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage, rejected_reason: reason || null }),
      });
      setApps(r.applications || []);
      if (r.stages) setAppStages(r.stages);
      setAppReject(null);
      if (r.notice) setAppNoticeIds([r.notice.id]);
      if (r.changed) {
        flash('Moved to ' + (target ? target.label : stage));
        try {
          const av = await authFetch('/api/candidates/' + candidateId + '/activity?limit=60');
          setActivity(av.events || []);
        } catch (e) { /* feed refresh is non-fatal */ }
      }
    } catch (e) {
      flash(e.message || 'Could not move the application');
    } finally { setAppBusy(null); }
  }, [authFetch, candidateId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await authFetch('/api/candidates/' + candidateId);
      setC(data.candidate || data);
      try {
        const np = await authFetch('/api/candidates/' + candidateId + '/notes?limit=20');
        setNotes(Array.isArray(np) ? np : np.notes || []);
        setNotesHasMore(!!np.has_more); setNotesCursor(np.next_before || null);
      } catch (e) { /* non-fatal */ }
      try { setDocs(await authFetch('/api/candidates/' + candidateId + '/documents')); } catch (e) { /* non-fatal */ }
      try { setSuppression(await authFetch('/api/candidates/' + candidateId + '/suppression')); } catch (e) { /* non-fatal */ }
      try {
        const av = await authFetch('/api/candidates/' + candidateId + '/activity?limit=60');
        setActivity(av.events || []);
      } catch (e) { setActivity([]); }
      try {
        try {
          const rl = await authFetch('/api/roles');
          const list = Array.isArray(rl) ? rl : (rl && rl.roles) || [];
          setOppRoles(list.filter(r => r.status === 'open'));
        } catch (e) { /* picker degrades to empty, the tab still works */ }
        const ap = await authFetch('/api/candidates/' + candidateId + '/applications');
        setApps(ap.applications || []);
        setAppStages(ap.stages || {});
      } catch (e) { setApps([]); }
      try {
        const hr = await authFetch('/api/candidates/' + candidateId + '/human-review');
        setReviews(hr.requests || []);
      } catch (e) { setReviews([]); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [authFetch, candidateId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        if (!c || !c.cv_file_path) return;
        const isPdf = /\.pdf$/i.test(c.cv_file_path);
        setCvIsPdf(isPdf);
        if (!isPdf) return; // DOCX: no native browser render, show extracted text instead
        const token = await getToken();
        const res = await fetch(API_BASE + '/api/candidates/' + candidateId + '/cv', { headers: { Authorization: 'Bearer ' + token } });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url; setCvUrl(url);
      } catch (e) { /* viewer is best-effort */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [c, candidateId, getToken]);

  async function saveEdit() {
    setSaving(true);
    try {
      await authFetch('/api/candidates/' + candidateId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editForm) });
      await load(); setEditing(false); flash('Saved');
    } catch (e) { flash(e.message); }
    setSaving(false);
  }

  async function reparse() {
    setReparsing(true);
    try { await authFetch('/api/candidates/' + candidateId + '/reparse', { method: 'POST' }); await load(); flash('Re-parsed'); }
    catch (e) { flash(e.message); }
    setReparsing(false);
  }

  async function viewCv() {
    try {
      const token = await getToken();
      const res = await fetch(API_BASE + '/api/candidates/' + candidateId + '/cv', { headers: { Authorization: 'Bearer ' + token } });
      if (!res.ok) throw new Error('could not fetch CV');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { flash(e.message); }
  }

  async function loadEarlierNotes() {
    if (!notesCursor || notesLoadingMore) return;
    setNotesLoadingMore(true);
    try {
      const np = await authFetch('/api/candidates/' + candidateId + '/notes?limit=20&before=' + encodeURIComponent(notesCursor));
      setNotes(prev => [...(np.notes || []), ...prev]);
      setNotesHasMore(!!np.has_more); setNotesCursor(np.next_before || null);
    } catch (e) { flash(e.message); }
    setNotesLoadingMore(false);
  }

  async function postNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const note = await authFetch('/api/candidates/' + candidateId + '/notes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body: newNote.trim(), author_name: userName || null, category: noteCat, direction: noteDir }) });
      setMyClerkId(note.author_clerk_id);
      setNotes(prev => [...prev, note]); setNewNote(''); setNoteDir(null);
      if (noteDir) refreshTemp(); // C1-EMAIL: the note moves their temperature
    } catch (e) { flash(e.message); }
    setSavingNote(false);
  }
  // C1-EMAIL: re-read the two contact dates after a note or an email.
  async function refreshTemp() {
    try { const d = await authFetch('/api/candidates/' + candidateId); const x = d.candidate || d; setC(prev => (prev ? { ...prev, last_contacted_at: x.last_contacted_at, last_contact_kind: x.last_contact_kind, last_responded_at: x.last_responded_at, last_response_kind: x.last_response_kind } : prev)); }
    catch (e) { /* the dot catches up on the next load */ }
  }
  async function deleteNote(noteId) {
    try { await authFetch('/api/candidates/' + candidateId + '/notes/' + noteId, { method: 'DELETE' }); setNotes(prev => prev.filter(n => n.id !== noteId)); refreshTemp(); }
    catch (e) { flash(e.message); }
  }

  async function uploadDoc(file) {
    if (!file) return;
    setDocBusy(true);
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('file', file); fd.append('kind', docKind);
      const res = await fetch(API_BASE + '/api/candidates/' + candidateId + '/documents', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'upload failed'); }
      setDocs(await authFetch('/api/candidates/' + candidateId + '/documents'));
      flash('Document added');
    } catch (e) { flash(e.message); }
    setDocBusy(false);
  }
  async function downloadDoc(doc) {
    try {
      const token = await getToken();
      const res = await fetch(API_BASE + '/api/candidates/' + candidateId + '/documents/' + doc.id + '/download', { headers: { Authorization: 'Bearer ' + token } });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = doc.original_name || 'document';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { flash(e.message); }
  }
  async function deleteDoc(docId) {
    try { await authFetch('/api/candidates/' + candidateId + '/documents/' + docId, { method: 'DELETE' }); setDocs(prev => prev.filter(d => d.id !== docId)); }
    catch (e) { flash(e.message); }
  }
  async function assignOwner(clear) {
    try { await authFetch('/api/candidates/' + candidateId + '/owner', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(clear ? { clear: true } : { owner_name: userName || 'Me' }) }); await load(); }
    catch (e) { flash(e.message); }
  }
  async function exportJson() {
    try {
      const token = await getToken();
      const res = await fetch(API_BASE + '/api/candidates/' + candidateId + '/export', { headers: { Authorization: 'Bearer ' + token } });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'candidate-' + candidateId + '-export.json';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { flash(e.message); }
  }
  async function doMerge() {
    const dupId = parseInt(mergeId, 10);
    if (!dupId) { flash('Enter the duplicate candidate ID'); return; }
    const confirmed = await gtConfirm({
      title: 'Merge duplicate record',
      message: [
        'Candidate #' + dupId + ' will be merged into this record.',
        'Notes, documents and tags move across, and the duplicate is retired.',
      ],
      confirmLabel: 'Merge',
      tone: 'danger',
    });
    if (!confirmed) return;
    setDangerBusy(true);
    try { await authFetch('/api/candidates/' + candidateId + '/merge', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ duplicate_id: dupId }) }); setMergeId(''); await load(); flash('Merged #' + dupId); }
    catch (e) { flash(e.message); }
    setDangerBusy(false);
  }
  async function doAnonymise() {
    const confirmed = await gtConfirm({
      title: 'Anonymise this record',
      message: [
        'This permanently strips every piece of personal data from the record, including notes, documents and the CV.',
        'It cannot be undone.',
      ],
      confirmLabel: 'Anonymise',
      tone: 'danger',
      requireWord: 'ERASE',
    });
    if (!confirmed) return;
    setDangerBusy(true);
    try { await authFetch('/api/candidates/' + candidateId + '/anonymise', { method: 'POST' }); flash('Anonymised'); await load(); }
    catch (e) { flash(e.message); }
    setDangerBusy(false);
  }

  if (loading) return <div className="gtr2"><GtLoader /></div>;
  if (error || !c) return <div className="gtr2"><div className="gtr2-top"><button type="button" className="gtr2-back" onClick={onBack}>← {backLabel || 'Candidates'}</button></div><div className="gtr2-empty">{error || 'Candidate not found.'}</div></div>;

  const parsed = c.cv_parsed || {};
  const flags = Array.isArray(c.cv_parse_flags) ? c.cv_parse_flags : [];
  const av = avatarColour(c.full_name);
  const liUrl = linkedinHref(c.linkedin_url); // C1-SEQ6: only a real LinkedIn address is a link
  const attr = c.source_attribution || {};
  const attrTitle = (field) => { const a2 = attr[field]; if (!a2) return undefined; const s = a2.source || a2.src || ''; const conf = a2.confidence != null ? Math.round(Number(a2.confidence) * 100) + '%' : ''; return ['From ' + s, conf && 'confidence ' + conf].filter(Boolean).join(', '); };

  const TL_CLS = {
    candidate: 'gtr-tl-teal', ai: 'gtr-tl-blue', compliance: 'gtr-tl-slate',
    delivery: 'gtr-tl-green', win: 'gtr-tl-green', loss: 'gtr-tl-slate',
    clientact: 'gtr-tl-blue', pipeline: 'gtr-tl-teal', role: 'gtr-tl-slate',
    client: 'gtr-tl-slate', intel: 'gtr-tl-blue',
  };
  const TL_ICON = {
    candidate: '\u25c8', ai: '\u25c7', compliance: '\u2696', delivery: '\u27a4',
    win: '\u2713', loss: '\u2715', clientact: '\u25c9', pipeline: '\u21e2',
  };

  const timeline = [];
  for (const n of notes) timeline.push({ when: n.created_at, cls: 'gtr-tl-teal', icon: '\u270e', title: 'Note by ' + (n.author_name || 'team'), text: n.body });

  if (activity.length) {
    // The real event spine: everything the candidate does in their own portal
    // lands here alongside staff actions. note_added is skipped because notes
    // are already rendered above with their full body.
    for (const ev of activity) {
      if (ev.action === 'note_added') continue;
      timeline.push({
        when: ev.at,
        cls: TL_CLS[ev.group] || 'gtr-tl-slate',
        icon: TL_ICON[ev.group] || '\u2022',
        title: ev.title + (ev.actor_display ? ' \u00b7 ' + ev.actor_display : ''),
        text: ev.detail_line || '',
      });
    }
  } else {
    // Fallback for records predating the spine, or if the feed call failed.
    if (c.last_enriched_at) timeline.push({ when: c.last_enriched_at, cls: 'gtr-tl-blue', icon: '\u25c7', title: 'Enriched', text: 'Record enriched from research.' });
    if (c.cv_file_path) timeline.push({ when: c.updated_at, cls: 'gtr-tl-green', icon: '\u2398', title: 'CV parsed', text: flags.length ? flags.length + ' field(s) flagged for review.' : 'Parsed clean.' });
    if (c.created_at) timeline.push({ when: c.created_at, cls: 'gtr-tl-slate', icon: '\uff0b', title: 'Added to the pool', text: 'Via ' + (LANE_LABEL[c.intake_lane] || c.intake_lane) + '.' });
  }
  timeline.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));

  const exp = Array.isArray(parsed.experience) ? parsed.experience : [];
  const edu = Array.isArray(parsed.education) ? parsed.education : [];
  const skills = Array.isArray(parsed.skills) ? parsed.skills : [];

  const statusLabel = String(c.status || 'new').replace(/_/g, ' ');
  const domainLabel = c.expertise_domain ? String(c.expertise_domain).replace(/_/g, ' ').replace(/^./, ch => ch.toUpperCase()) : null;
  const rateLabel = formatRate(c.expected_rate_gbp, c.expected_rate_unit);
  const openReviews = reviews.filter(v => v.status !== 'closed');
  const tabCount = { Notes: notes.length, Applications: apps.length + openReviews.length };
  const startEdit = () => { setEditForm(Object.fromEntries(EDIT_FIELDS.map(([k]) => [k, c[k] || '']))); setEditing(true); };
  const toggleMenu = (m) => setMenu(cur => (cur === m ? null : m));
  const patchField = async (body, done) => {
    try {
      await authFetch('/api/candidates/' + candidateId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      await load(); flash(done);
    } catch (err) { flash(err.message); }
  };

  return (
    <>
      <div className="gtr2">
        <div className="gtr2-top">
          <button type="button" className="gtr2-back" onClick={onBack}><Ico d={ICONS.back} />{backLabel || 'Candidates'}</button>
          <span className="gtr2-sep">/</span>
          <span className="gtr2-crumb">{c.full_name || 'Unnamed'}</span>
          {c.ref && <span className="gtr2-ref">{c.ref}</span>}
        </div>

        {suppression && suppression.suppressed && (
          <div className="gtr2-banner gtr2-banner--rose">
            <Ico d={ICONS.block} />
            <span><b>Think before emailing.</b> This address is on the suppression list ({suppression.type}{suppression.reason ? ': ' + suppression.reason : ''}). You will see this again before anything sends.</span>
          </div>
        )}
        {pdfErr && <div className="gtr2-banner gtr2-banner--amber"><Ico d={ICONS.alert} /><span>{pdfErr}</span></div>}
        {toast && <div className="gtc-toast">{toast}</div>}

        <section className="gtr2-hero">
          <div className="gtr2-cover" />
          <div className="gtr2-hero-row">
            <span className="gtr2-avatar" style={{ background: av }}>{initials(c.full_name)}</span>
            <div className="gtr2-id">
              <div className="gtr2-namerow">
                <h1 className="gtr2-name">{c.full_name || 'Unnamed'}</h1>
                <span className={'gtt-pill gtr2-status ' + (STATUS_PILL[c.status] || 'gtt-p-slate')}>{statusLabel}</span>
              </div>
              <p className="gtr2-headline">
                {c.headline || c.current_title || 'No headline yet'}
                {c.current_company ? <span className="gtr2-at"> at <b>{c.current_company}</b></span> : null}
              </p>
              <div className="gtr2-chips">
                <span className="gtr2-chip gtr2-chip--temp"><TempHistory row={c} candidateId={candidateId} authFetch={authFetch} /></span>{/* C1-EMAIL */}
                {c.location && <span className="gtr2-chip" title={attrTitle('location')}><Ico d={ICONS.pin} />{c.location}</span>}
                {domainLabel && <span className="gtr2-chip gtr2-chip--domain"><Ico d={ICONS.spark} />{domainLabel}</span>}
                {rateLabel && <span className="gtr2-chip"><Ico d={ICONS.rate} />{rateLabel}</span>}
                {c.availability_date && <span className="gtr2-chip"><Ico d={ICONS.cal} />From {ukDate(c.availability_date)}</span>}
                {c.email && <a className="gtr2-chip gtr2-chip--link" href={'mailto:' + c.email}><Ico d={ICONS.mail} />{c.email}</a>}
                {liUrl && <a className="gtr2-chip gtr2-chip--link" href={liUrl} target="_blank" rel="noreferrer"><Ico d={ICONS.link} />LinkedIn</a>}
                {c.github_username && <a className="gtr2-chip gtr2-chip--link" href={'https://github.com/' + c.github_username} target="_blank" rel="noreferrer"><Ico d={ICONS.code} />{c.github_username}</a>}
              </div>
            </div>
            <div className="gtr2-acts">
              <button type="button" className="gtr2-btn gtr2-btn--primary" onClick={() => setDossierOpen(true)}><Ico d={ICONS.doc} />View dossier</button>
              <div className={'gtr2-menuwrap' + (menu === 'pdf' ? ' gtr2-menuwrap--open' : '')}>
                <button type="button" className="gtr2-btn" aria-haspopup="menu" aria-expanded={menu === 'pdf'} disabled={!!pdfBusy} onClick={() => toggleMenu('pdf')}>
                  <Ico d={ICONS.down} />{pdfBusy ? 'Making PDF…' : 'PDF'}<Ico d={ICONS.caret} cls="gtr2-caret" />
                </button>
                {menu === 'pdf' && (
                  <div className="gtr2-menu" role="menu">
                    <button type="button" role="menuitem" onClick={() => { setMenu(null); makePdf(false); }}>
                      <b>Named PDF</b><span>The full dossier with their name</span>
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenu(null); makePdf(true); }}>
                      <b>Anonymous PDF</b><span>No name, contact details or links</span>
                    </button>
                  </div>
                )}
              </div>
              <button type="button" className="gtr2-btn" onClick={() => setEmailOpen(true)} title={c.email ? 'Email ' + c.email : 'No email address on this record'} disabled={!c.email}><Ico d={ICONS.mail} />Email</button>{/* C1-EMAIL */}
              <button type="button" className="gtr2-btn" onClick={() => setBenchAdd(true)}><Ico d={ICONS.plus} />Add to bench</button>
              <div className={'gtr2-menuwrap' + (menu === 'more' ? ' gtr2-menuwrap--open' : '')}>
                <button type="button" className="gtr2-btn gtr2-btn--icon" aria-label="More actions" aria-haspopup="menu" aria-expanded={menu === 'more'} onClick={() => toggleMenu('more')}>
                  <Ico d={ICONS.dots} cls="gtr2-ico--dots" />
                </button>
                {menu === 'more' && (
                  <div className="gtr2-menu gtr2-menu--right" role="menu">
                    {canImpersonate && (
                      <button type="button" role="menuitem" disabled={impBusy} onClick={() => { setMenu(null); startImpersonation(); }}>
                        <b>{impBusy ? 'Opening…' : 'View as candidate'}</b><span>Open their portal as they see it</span>
                      </button>
                    )}
                    <button type="button" role="menuitem" onClick={() => { setMenu(null); startEdit(); }}>
                      <b>Edit details</b><span>Change their key information</span>
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenu(null); setTab('Record'); }}>
                      <b>Record and compliance</b><span>Consent, retention, merge and erasure</span>
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenu(null); exportJson(); }}>
                      <b>Export all data</b><span>Everything we hold, as JSON</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="gtr2-stats">
            <Stat label="Applications" value={apps.length} />
            <Stat label="Benches" value={onBenches === null ? '…' : onBenches.length} />
            <Stat label="Notes" value={notes.length} />
            <Stat label="Enriched" value={c.last_enriched_at ? ukDate(c.last_enriched_at) : 'Not yet'} />
            <Stat label="Added" value={ukDate(c.created_at) || '—'} sub={LANE_LABEL[c.intake_lane] || c.intake_lane || null} />
            <Stat label="Owner" value={c.owner_name || (c.owner_clerk_id ? 'Assigned' : 'Nobody')} />
          </div>
        </section>
        {menu && <button type="button" className="gtr2-scrim" aria-label="Close menu" onClick={() => setMenu(null)} />}

        <div className="gtr2-body">
          <div className="gtr2-main">
            <div className="gtr-card gtr2-tabcard">
              <div className="gtr2-tabs" role="tablist">
                {TABS.filter((t) => t !== 'Lexa' || canSeeLexa).map(t => (
                  <button type="button" role="tab" aria-selected={tab === t} key={t} className={'gtr2-tab' + (tab === t ? ' gtr2-tab--on' : '')} onClick={() => setTab(t)}>
                    {t}{tabCount[t] ? <span className="gtr2-count">{tabCount[t]}</span> : null}
                  </button>
                ))}
              </div>

              <div className="gtr2-tabbody">
                {tab === 'Activity' && (
                  <div className="gtr-timeline">
                    {notesHasMore && (
                      <button className="gtr-showmore" onClick={loadEarlierNotes} disabled={notesLoadingMore}>
                        {notesLoadingMore ? 'Loading…' : 'Show earlier activity'}
                      </button>
                    )}
                    {timeline.length === 0 && <div className="gtr2-empty">No activity yet.</div>}
                    {timeline.map((it, i) => (
                      <div className="gtr-tlitem" key={i}>
                        <span className={'gtr-tldot ' + it.cls}>{it.icon}</span>
                        <div className="gtr-tlbody">
                          <div className="gtr-tltop"><span className="gtr-tlwho">{it.title}</span><span className="gtr-tlwhen">{relTime(it.when)}</span></div>
                          {it.text && <div className="gtr-tltext">{it.text}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {tab === 'CV' && (
                  <div className="gtr-cv">
                    {!c.cv_file_path && <div className="gtr2-empty">No CV on file for this candidate.</div>}
                    {c.cv_file_path && cvIsPdf && cvUrl && (
                      <div className="gtr-cvsec"><iframe src={cvUrl} title="CV" className="gtr-cvframe" /></div>
                    )}
                    {c.cv_file_path && !cvIsPdf && (
                      <div className="gtr-cvsec">
                        <button className="gtc-colbtn" onClick={() => setShowRawCv(v => !v)}>{showRawCv ? 'Hide extracted text' : 'Show extracted text'}</button>
                        <span className="gtr-cvim" style={{ marginLeft: 8 }}>Original is a Word file. Download it with View original.</span>
                        {showRawCv && c.cv_text && <pre className="gtr-cvraw">{c.cv_text}</pre>}
                      </div>
                    )}
                    {skills.length > 0 && (
                      <div className="gtr-cvsec">
                        <div className="gtr-cvsech">Skills</div>
                        <div className="gtr-skillwrap">{skills.slice(0, 24).map((s, i) => { const label = typeof s === 'string' ? s : s.name || ''; return <span key={i} className="gtr2-skill">{label}</span>; })}</div>
                      </div>
                    )}
                    {exp.length > 0 && (
                      <div className="gtr-cvsec">
                        <div className="gtr-cvsech">Experience</div>
                        {exp.map((x, i) => (
                          <div className="gtr-cvitem" key={i}>
                            <div className="gtr-cvit"><b>{x.title || x.role || ''}</b>{x.company ? ' · ' + x.company : ''}</div>
                            <div className="gtr-cvim">{[x.start_date || x.start, x.end_date || x.end || 'present'].filter(Boolean).join(' – ')}</div>
                            {x.description && <div className="gtr-cvid">{String(x.description).slice(0, 300)}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {edu.length > 0 && (
                      <div className="gtr-cvsec">
                        <div className="gtr-cvsech">Education</div>
                        {edu.map((x, i) => (
                          <div className="gtr-cvitem" key={i}>
                            <div className="gtr-cvit"><b>{x.institution || x.school || ''}</b></div>
                            <div className="gtr-cvim">{[x.degree, x.field].filter(Boolean).join(', ')}{x.end_date || x.end ? ' · ' + (x.end_date || x.end) : ''}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {c.cv_file_path && skills.length === 0 && exp.length === 0 && edu.length === 0 && (
                      <div className="gtr2-empty">The CV is on file but no structured sections were extracted. Try Re-parse.</div>
                    )}
                  </div>
                )}

                {tab === 'Notes' && (
                  <div className="gtr-notes">
                    {notes.length > 0 && (
                      <div className="gtnc-filter">{/* C1-EMAIL */}
                        {[['all', 'All']].concat(NOTE_CATS).map(([k, l]) => {
                          const count = k === 'all' ? notes.length : notes.filter(nt => (nt.category || 'general') === k).length;
                          if (k !== 'all' && !count) return null;
                          return <button type="button" key={k} className={'gtnc-chip gtnc-chip--' + k + (noteFilter === k ? ' gtnc-chip--on' : '')} onClick={() => setNoteFilter(k)}>{l}<span>{count}</span></button>;
                        })}
                      </div>
                    )}
                    <div className="gtr-notelist">
                      {notesHasMore && (
                        <button className="gtr-showmore" onClick={loadEarlierNotes} disabled={notesLoadingMore}>
                          {notesLoadingMore ? 'Loading…' : 'Show earlier'}
                        </button>
                      )}
                      {notes.length === 0 && <div className="gtr2-empty">No notes yet. Add the first one below.</div>}
                      {notes.filter(nt => noteFilter === 'all' || (nt.category || 'general') === noteFilter).map(note => {
                        const isMe = myClerkId ? note.author_clerk_id === myClerkId : false;
                        const ac = avatarColour(note.author_name || note.author_clerk_id);
                        return (
                          <div className={'gtr-noterow' + (isMe ? ' gtr-note-me' : '')} key={note.id}>
                            {isMe && userImage
                              ? <img className="gtr-noteav gtr-noteav-img" src={userImage} alt="" />
                              : <span className="gtr-noteav" style={{ background: isMe ? '#1FA9B2' : ac }}>{initials(note.author_name || 'T')}</span>}
                            <div className="gtr-notebody">
                              <div className="gtr-notemeta"><span>{note.author_name || 'Team'}</span>{note.category && note.category !== 'general' ? <span className={'gtnc-tag gtnc-chip--' + note.category}>{NOTE_CAT_LABEL[note.category]}</span> : null}{note.direction ? <span className={'gtnc-dir gtnc-dir--' + note.direction}>{note.direction === 'in' ? 'They replied' : 'I messaged them'}</span> : null}<span className="gtr-notetime">{note.created_at ? new Date(note.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></div>
                              <div className={'gtr-notebubble' + (isMe ? ' gtr-notebubble-me' : '')}>
                                {note.body}
                                <button className="gtr-notedel" title="Delete note" aria-label="Delete note" onClick={() => deleteNote(note.id)}>×</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="gtnc-compose">{/* C1-EMAIL */}
                      <div className="gtnc-cats">{NOTE_CATS.map(([k, l]) => <button type="button" key={k} className={'gtnc-chip gtnc-chip--' + k + (noteCat === k ? ' gtnc-chip--on' : '')} onClick={() => setNoteCat(k)}>{l}</button>)}</div>
                      <div className="gtnc-dirs">
                        <button type="button" className={'gtnc-dirbtn' + (noteDir === 'out' ? ' gtnc-dirbtn--on' : '')} title="Counts as contact: makes them warm" onClick={() => setNoteDir(d => (d === 'out' ? null : 'out'))}>I messaged them</button>
                        <button type="button" className={'gtnc-dirbtn gtnc-dirbtn--in' + (noteDir === 'in' ? ' gtnc-dirbtn--on' : '')} title="Counts as a response: makes them hot" onClick={() => setNoteDir(d => (d === 'in' ? null : 'in'))}>They replied</button>
                      </div>
                    </div>
                    <div className="gtr-notecompose">
                      <textarea value={newNote} onChange={e => setNewNote(e.target.value)} aria-label="New note"
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postNote(); } }}
                        placeholder={noteCat === 'linkedin' ? 'Paste the LinkedIn conversation, or add a note.' : 'Add a note. Enter to send, Shift+Enter for a new line.'} rows={2} />
                      <button className="gtc-primary" onClick={postNote} disabled={savingNote || !newNote.trim()}>{savingNote ? '…' : 'Send'}</button>
                    </div>
                  </div>
                )}

                {tab === 'Applications' && (
                  <div className="gtr-apps">
                    {openReviews.map(v => (
                      <div key={'hr' + v.id} className="gtr-hrcard">
                        <div className="gtr-hrhead">
                          <span className="gtr-hrtag">Human review requested</span>
                          <span className="gtr-hrwhen">{relTime(v.created_at)}</span>
                        </div>
                        <p className="gtr-hrreason">
                          {v.reason || 'No reason given, which is their right.'}
                        </p>
                        {v.role_title && <div className="gtr-appmeta">{v.role_title}{v.application_ref ? ' · ' + v.application_ref : ''}</div>}
                        <textarea className="gtr-hrnote" rows={2} value={reviewNote} aria-label="Review decision"
                          onChange={e => setReviewNote(e.target.value)}
                          placeholder="What a person decided, and why" />
                        <div className="gtr-hractions">
                          {v.status === 'open' && (
                            <button className="gtc-tb-btn" disabled={reviewBusy === v.id}
                              onClick={() => moveReview(v.id, 'in_review', reviewNote)}>Pick up</button>
                          )}
                          <button className="gtc-primary" disabled={reviewBusy === v.id}
                            onClick={() => moveReview(v.id, 'closed', reviewNote)}>
                            {reviewBusy === v.id ? 'Saving' : 'Close and email them the outcome'}
                          </button>
                        </div>
                      </div>
                    ))}
                    {apps.length === 0 && (
                      <div className="gtr2-empty">
                        No applications yet. Roles this candidate applies for through the portal appear
                        here, and moving the stage here updates what they see.
                      </div>
                    )}
                    {apps.map(a => (
                      <Fragment key={a.id}>
                      <div className="gtr-approw">
                        <div className="gtr-appmain">
                          <div className="gtr-apptitle">{a.role.title}</div>
                          <div className="gtr-appmeta">
                            {[a.client.name, a.ref, a.applied_at ? 'applied ' + relTime(a.applied_at) : null]
                              .filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <div className="gtr-appctl">
                          <GtSelect
                            value={a.pipeline && a.pipeline.stage}
                            placeholder="Not on the board"
                            options={(appStages[a.role.ref] || []).map(x => ({ value: x.key, label: x.label }))}
                            onChange={v => moveApp(a, v, null, appStages[a.role.ref])}
                            width={190}
                          />
                          <span className="gtr-appseen">They see: {a.status_label}</span>
                          {appBusy === a.id && <span className="gtr-appbusy">Saving</span>}
                        </div>
                      </div>
                      {appReject && appReject.id === a.id && (() => {
                        const t = (appStages[a.role.ref] || []).find(x => x.key === appReject.stage);
                        return (
                          <div className="gtpl-reject gtr-appreject">
                            <input value={appReason} autoFocus
                              aria-label={'Reason for moving to ' + (t ? t.label : 'this stage')}
                              placeholder={t && t.meaning === 'withdrawn' ? 'Why did they pull out? One line.' : 'Why are they out? One line.'}
                              onChange={e => setAppReason(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && appReason.trim()) moveApp(a, appReject.stage, appReason, appStages[a.role.ref]);
                                if (e.key === 'Escape') setAppReject(null);
                              }} />
                            <button className="gtc-btn-ghost" onClick={() => setAppReject(null)}>Cancel</button>
                            <button className="gtc-primary" disabled={!appReason.trim()}
                              onClick={() => moveApp(a, appReject.stage, appReason, appStages[a.role.ref])}>
                              {'Move to ' + (t ? t.label : 'this stage')}
                            </button>
                          </div>
                        );
                      })()}
                      </Fragment>
                    ))}

                    <div className="gtr-opp">
                      <div className="gtr-opptitle">Tell this candidate about a role</div>
                      <div className="gtr-opphint">
                        This is a marketing message, not an update about an application they
                        made. It is only sent if they have asked to hear about new roles.
                      </div>
                      <div className="gtr-opprow">
                        <GtSelect
                          value={oppRole}
                          options={[{ value: '', label: oppRoles.length ? 'Choose an open role' : 'No open roles' }]
                            .concat(oppRoles.map(r => ({
                              value: String(r.id),
                              label: r.title + (r.client_name ? ' · ' + r.client_name : ''),
                            })))}
                          onChange={chooseOppRole}
                          width={320}
                        />
                        <button className="gtc-primary" disabled={!oppRole || oppBusy || oppDrafting || !oppNote.trim()}
                          onClick={sendOpportunity}>
                          {oppBusy ? 'Sending' : oppDrafting ? 'Drafting' : 'Send'}
                        </button>
                      </div>
                      {oppDrafting && (
                        <div className="gtr-oppdrafting">
                          Reading their CV and profile against the role, and writing the message.
                        </div>
                      )}
                      {oppDraftError && (
                        <div className="gtr-oppres gtr-oppres--no">{oppDraftError}</div>
                      )}
                      {!oppDrafting && oppNote ? (
                        <div className="gtr-oppeditor">
                          <GtEditor
                            value={oppNote}
                            onChange={setOppNote}
                            minHeight={260}
                            placeholder="The message that will be sent"
                          />
                        </div>
                      ) : null}
                      {oppResult && (
                        <div className={'gtr-oppres gtr-oppres--' + (oppResult.sent ? 'ok' : 'no')}>
                          {oppResult.sent
                            ? 'Sent.'
                            : oppResult.reason === 'preference_off'
                              ? 'Not sent. They have turned off emails about new roles.'
                              : oppResult.reason === 'unsubscribed'
                                ? 'Not sent. They have unsubscribed from everything except updates on their own applications.'
                                : oppResult.reason === 'consent_withdrawn'
                                  ? 'Not sent. They have withdrawn consent.'
                                  : oppResult.reason === 'email_channel_off'
                                    ? 'Not sent. Email is switched off for this candidate.'
                                    : oppResult.reason === 'duplicate'
                                      ? 'Not sent. They were told about this role in the last few minutes.'
                                      : oppResult.reason === 'no_email'
                                        ? 'Not sent. No email address on record.'
                                        : oppResult.reason === 'not_published'
                                          ? 'Not sent. This role is not on the public job board, so the apply link would go nowhere. Publish it first.'
                                        : 'Not sent. ' + (oppResult.reason || 'unknown reason') + '.'}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {tab === 'Interviews' && c && ( // C2-IVFIX
                  <GtInterviewsPanel kind="candidate" ownerId={c.id} getToken={getToken} flash={flash} />
                )}

                {tab === 'Lexa' && canSeeLexa && c && (
                  <GtCandidateLexa candidateId={c.id} getToken={getToken} />
                )}

                {tab === 'Emails' && c && ( // C1-EMAIL
                  <GtCandidateEmails candidate={c} authFetch={authFetch} onChanged={refreshTemp} />
                )}

                {tab === 'Forms' && (
                  <GtCandidateForms candidateId={candidateId} authFetch={authFetch} />
                )}

                {tab === 'Sequences' && c && ( // C1-SEQ3
                  <GtCandidateSequences candidate={c} authFetch={authFetch} navigate={navigate} />
                )}

                {tab === 'Record' && (
                  <div className="gtr2-record">
                    <section className="gtr2-panel">
                      <h3>Pool record</h3>
                      <div className="gtr-props">
                        <div className="gtr-prop"><span className="gtr-prop-k">Owner</span>
                          <span className="gtr-prop-v">{c.owner_name || (c.owner_clerk_id ? 'Assigned' : '—')}{' '}
                            {c.owner_clerk_id
                              ? <button className="gtr-linkbtn" onClick={() => assignOwner(true)}>clear</button>
                              : <button className="gtr-linkbtn" onClick={() => assignOwner(false)}>assign to me</button>}
                          </span></div>
                        <Prop k="Candidate ID" v={'#' + c.id} />
                        <Prop k="Portal account" v={c.clerk_user_id ? 'Active' : 'Not signed up'} />
                        <Prop k="Import batch" v={c.import_id ? '#' + c.import_id : '—'} />
                        <Prop k="Enriched" v={c.last_enriched_at ? ukDate(c.last_enriched_at) : 'Not yet'} />
                        <Prop k="Match embedding" v={c.embedding ? 'Ready' : 'Pending synthesis'} />
                        <Prop k="Created" v={ukDate(c.created_at) || '—'} />
                        <Prop k="Updated" v={ukDate(c.updated_at) || '—'} />
                      </div>
                    </section>

                    <section className="gtr2-panel">
                      <h3>Compliance</h3>
                      <div className="gtr-props">
                        <div className="gtr-prop gtr2-prop-ctl"><span className="gtr-prop-k">Consent</span>
                          <GtSelect width={150} value={c.consent_status || 'none'} options={[{ value: 'none', label: 'None recorded' }, { value: 'explicit', label: 'Explicit' }, { value: 'withdrawn', label: 'Withdrawn' }]}
                            onChange={v => patchField({ consent_status: v }, 'Consent updated')} /></div>
                        <Prop k="Consent source" v={c.consent_source || '—'} />
                        <div className="gtr-prop gtr2-prop-ctl"><span className="gtr-prop-k">Retention expiry</span>
                          <GtDatePicker width={170} value={c.retention_expires_at ? String(c.retention_expires_at).slice(0, 10) : null}
                            onChange={v => patchField({ retention_expires_at: v }, 'Retention updated')} /></div>
                        <Prop k="Email check" v={c.email_verification_result || 'pending'} />
                      </div>
                    </section>

                    <section className="gtr2-panel gtr2-panel--danger">
                      <h3>Data and erasure</h3>
                      <div className="gtr2-dangerrow">
                        <div><b>Export all data</b><span>Everything we hold on them, as a JSON file.</span></div>
                        <button className="gtr2-btn" onClick={exportJson}>Export</button>
                      </div>
                      <div className="gtr2-dangerrow">
                        <div><b>Merge a duplicate</b><span>Notes, documents and tags move into this record.</span></div>
                        <div className="gtr2-mergebox">
                          <input className="gtr2-input" aria-label="Duplicate candidate ID" placeholder="Duplicate ID, e.g. 7" value={mergeId} onChange={e => setMergeId(e.target.value)} />
                          <button className="gtr2-btn" disabled={dangerBusy} onClick={doMerge}>Merge</button>
                        </div>
                      </div>
                      <div className="gtr2-dangerrow">
                        <div><b>Anonymise</b><span>Right to erasure. Strips all personal data. Cannot be undone.</span></div>
                        <button className="gtr2-btn gtr2-btn--danger" disabled={dangerBusy} onClick={doAnonymise}>Anonymise</button>
                      </div>
                    </section>
                  </div>
                )}
              </div>
            </div>

            <GtAiCard candidateId={candidateId} getToken={getToken} />
          </div>

          <aside className="gtr2-side">
            <div className="gtr-card">
              <div className="gtr-cardh">Key information {!editing && <button type="button" className="gtr-cardh-edit" onClick={startEdit}>Edit</button>}</div>
              {editing ? (
                <div className="gtr-editform">
                  {EDIT_FIELDS.map(([key, label]) => (
                    <div key={key} className="gtr-editfield"><label>{label}</label>
                      {EDIT_SELECTS[key]
                        ? <GtSelect value={editForm[key] || ''} options={EDIT_SELECTS[key]} onChange={v => setEditForm(p => ({ ...p, [key]: v }))} />
                        : EDIT_DATES[key]
                          ? <GtDatePicker value={editForm[key] ? String(editForm[key]).slice(0, 10) : null} onChange={v => setEditForm(p => ({ ...p, [key]: v || '' }))} />
                          : <input aria-label={label} value={editForm[key] || ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))} />}
                    </div>
                  ))}
                  <div className="gtr-editactions">
                    <button className="gtc-primary" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                    <button className="gtc-colbtn" onClick={() => setEditing(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="gtr-props">
                  <Prop title={attrTitle('email')} k="Email" v={c.email ? <a className="gtt-link" href={'mailto:' + c.email}>{c.email}</a> : '—'} />
                  <Prop title={attrTitle('phone')} k="Phone" v={c.phone || '—'} />
                  <Prop k="LinkedIn" v={liUrl ? <a className="gtt-link" href={liUrl} target="_blank" rel="noreferrer">{(c.linkedin_url || '').replace(/^https?:\/\/(www\.)?linkedin\.com\//, '').replace(/\/$/, '')}</a>
                    : c.linkedin_url ? <span title="This is not a LinkedIn address. Edit the record to paste their profile link.">{c.linkedin_url} (not a link)</span> : '—'} />{/* C1-SEQ6 */}
                  <Prop k="GitHub" v={c.github_username ? <a className="gtt-link" href={'https://github.com/' + c.github_username} target="_blank" rel="noreferrer">{c.github_username}</a> : '—'} />
                  <Prop title={attrTitle('location')} k="Location" v={c.location || '—'} />
                  <Prop k="Expertise" v={domainLabel || '—'} />
                  <Prop k="Right to work" v={c.right_to_work_status ? (RTW_LABEL[c.right_to_work_status] || c.right_to_work_status) + (c.right_to_work_country ? ' (' + c.right_to_work_country + ')' : '') : '—'} />
                  <Prop k="Expected rate" v={rateLabel || '—'} />
                  <Prop k="Available from" v={ukDate(c.availability_date) || '—'} />
                  <Prop k="Notice period" v={c.notice_period || '—'} />
                  <Prop k="Intake" v={LANE_LABEL[c.intake_lane] || c.intake_lane || '—'} />
                </div>
              )}
            </div>

            <GtEnrichCard key={'enrich-' + ((c && c.linkedin_url) || '')} candidateId={candidateId} getToken={getToken} onChanged={load} />

            <div className="gtr-card">
              <div className="gtr-cardh">On benches <button type="button" className="gtr-cardh-edit" onClick={() => setBenchAdd(true)}>Add</button></div>
              {onBenches === null ? <div className="gtr2-empty">Loading…</div> : onBenches.length === 0 ? (
                <div className="gtr2-empty">Not on any bench.</div>
              ) : (
                <div className="gtr2-list">
                  {onBenches.map(b => (
                    <button type="button" key={b.member_id} className="gtr2-listrow" onClick={() => navigate('/benches/' + b.ref)}>
                      <span className="gtr2-listico"><Ico d={ICONS.layers} /></span>
                      <span className="gtr2-listmain">
                        <b>{b.name}{b.status === 'archived' ? ' (archived)' : ''}</b>
                        <span>{(b.client_name || 'No client') + ' · added ' + new Date(b.added_at).toLocaleDateString('en-GB')}</span>
                      </span>
                      <span className={'gtbn-recbucket gtr2-bucket gtbn-tone-' + (b.bucket_tone || 'none')}>{b.bucket || 'No bucket'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="gtr-card">
              <div className="gtr-cardh">CV and documents</div>
              <div className="gtr2-filerow">
                <span className="gtr2-fileico"><Ico d={ICONS.doc} /></span>
                <span className="gtr2-filemain">
                  <b>CV</b>
                  <span>{c.cv_file_path ? (flags.length ? flags.length + ' field' + (flags.length === 1 ? '' : 's') + ' to check' : 'Parsed clean') : 'No CV on file'}</span>
                </span>
                {c.cv_file_path && <span className={'gtt-pill ' + (flags.length ? 'gtt-p-amber' : 'gtt-p-green')}>{flags.length ? 'Flagged' : 'Parsed'}</span>}
              </div>
              {c.cv_file_path && (
                <div className="gtr2-filebtns">
                  <button className="gtr2-btn gtr2-btn--sm" onClick={viewCv}>View original</button>
                  <button className="gtr2-btn gtr2-btn--sm" onClick={reparse} disabled={reparsing}>{reparsing ? 'Re-parsing…' : 'Re-parse'}</button>
                </div>
              )}
              {flags.length > 0 && (
                <div className="gtr2-flags">
                  <b>The parser was unsure about</b>
                  <ul>{flags.map((f, i) => <li key={i}>{typeof f === 'string' ? f.replace(/_/g, ' ') : (f.field || JSON.stringify(f))}</li>)}</ul>
                </div>
              )}
              {docs.map(d => (
                <div key={d.id} className="gtr2-filerow">
                  <span className="gtr2-fileico gtr2-fileico--doc"><Ico d={ICONS.clip} /></span>
                  <span className="gtr2-filemain">
                    <b>{d.label || d.original_name}</b>
                    <span>{String(d.kind).replace(/_/g, ' ')}</span>
                  </span>
                  <span className="gtr2-fileacts">
                    <button className="gtr-linkbtn" onClick={() => downloadDoc(d)}>Download</button>
                    <button className="gtr-linkbtn gtr-linkbtn-red" onClick={() => deleteDoc(d.id)}>Delete</button>
                  </span>
                </div>
              ))}
              <div className="gtr2-upload">
                <GtSelect width={150} value={docKind} onChange={setDocKind}
                  options={[{ value: 'rtw_evidence', label: 'RTW evidence' }, { value: 'portfolio', label: 'Portfolio' }, { value: 'reference', label: 'Reference' }, { value: 'certificate', label: 'Certificate' }, { value: 'other', label: 'Other' }]} />
                <label className="gtr2-btn gtr2-btn--sm gtr2-uploadbtn">
                  <Ico d={ICONS.plus} />{docBusy ? 'Uploading…' : 'Add document'}
                  <input type="file" style={{ display: 'none' }} disabled={docBusy} onChange={e => uploadDoc(e.target.files[0])} />
                </label>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {dossierOpen && <GtDossierView candidateId={candidateId} getToken={getToken} onClose={() => setDossierOpen(false)} />}
      {emailOpen && c && ( // C1-EMAIL
        <GtEmailModal candidateIds={[c.id]} source={{ kind: 'candidate', id: c.id, label: c.full_name || c.ref }} authFetch={authFetch}
          onClose={() => { setEmailOpen(false); refreshTemp(); }} />
      )}
      {benchAdd && (
        <GtAddToBenchModal candidateIds={[c.id]} authFetch={authFetch} flash={flash} navigate={navigate}
          source="record" onClose={() => setBenchAdd(false)} onDone={loadBenches} />
      )}
      {appNoticeIds && (
        <GtNoticeModal ids={appNoticeIds} authFetch={authFetch} onClose={() => setAppNoticeIds(null)} />
      )}
    </>
  );
}

// C1-SEQ6: a real LinkedIn link, or null. A full linkedin.com address, or a
// bare profile handle ("jane-smith-1a2b"). Anything else (a name typed into
// the field, say) is not a link.
function linkedinHref(v) {
  const s = String(v || '').trim();
  if (!s) return null;
  if (/^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/\S+$/i.test(s)) return /^https?:\/\//i.test(s) ? s : 'https://' + s;
  if (/^[A-Za-z0-9][A-Za-z0-9_%-]{2,99}$/.test(s)) return 'https://www.linkedin.com/in/' + s;
  return null;
}

function Prop({ k, v, title }) { return <div className="gtr-prop" title={title}><span className="gtr-prop-k">{k}{title ? <span className="gtr-attr-dot" /> : null}</span><span className="gtr-prop-v">{v}</span></div>; }

function Stat({ label, value, sub }) {
  return <div className="gtr2-stat"><span>{label}</span><b>{value}</b>{sub ? <em>{sub}</em> : null}</div>;
}

// Line icons, 24px grid, drawn with a stroke so they sit with the type.
const ICONS = {
  back: 'M15 18l-6-6 6-6',
  pin: 'M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  spark: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
  rate: 'M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  cal: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  code: 'M8 7l-5 5 5 5M16 7l5 5-5 5',
  doc: 'M6 3h8l4 4v14H6zM14 3v4h4M9 13h6M9 17h6',
  down: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  caret: 'M7 10l5 5 5-5',
  plus: 'M12 5v14M5 12h14',
  dots: 'M5 12h.01M12 12h.01M19 12h.01',
  block: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM5.6 5.6l12.8 12.8',
  alert: 'M12 3l10 18H2zM12 10v5M12 18h.01',
  chat: 'M4 5h16v11H9l-5 4z',
  send: 'M4 12l16-8-6 16-2-7z',
  layers: 'M12 3l9 5-9 5-9-5zM3 13l9 5 9-5',
  clip: 'M20 11l-8.5 8.5a5 5 0 0 1-7-7L13 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L14 7',
};
function Ico({ d, cls }) {
  return (
    <svg className={'gtr2-ico' + (cls ? ' ' + cls : '')} viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}
